import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useBulkUpdatePartFiles } from '@frontend/api';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  ButtonGroup,
  Checkbox,
  Tooltip,
  useToast,
  ModalCloseButton,
} from '@frontend/legacy-components';

import { fileViewer } from '../data/actions';
import { getActiveRootFile } from '../data/reducer';

type UpdateItarModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const UpdateItarModal = ({ isOpen, onClose }: UpdateItarModalProps) => {
  const dispatch = useDispatch();
  const activeFile = useSelector(getActiveRootFile);
  const [isChecked, setIsChecked] = useState(!!activeFile?.isExportControlled);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const toast = useToast();
  const { mutate: bulkUpdatePartFiles, isLoading: areFilesUpdating } =
    useBulkUpdatePartFiles({
      queryOptions: {
        onSuccess: () => {
          dispatch(
            fileViewer.updateItarStatus({
              isExportControlled: isChecked,
            }),
          );
          toast({
            status: 'success',
            title: `File ${
              isChecked ? '' : 'un'
            }marked as Export Controlled (ITAR)`,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: `Unable to ${
              isChecked ? '' : 'un'
            }mark file as Export Controlled (ITAR)`,
            description: `Check your connection and try again`,
          });
        },
        onSettled: () => {
          onClose();
        },
      },
    });

  const performBulkUpdate = useCallback(() => {
    const payload = [
      {
        uuid: activeFile?.uuid,
        isExportControlled: isChecked,
      },
    ];
    bulkUpdatePartFiles({ partFiles: payload });
  }, [activeFile?.uuid, bulkUpdatePartFiles, isChecked]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent maxWidth="510px">
        <ModalHeader>
          Edit ITAR status
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Text mb={1}>
            Marking this file as Export Controlled (ITAR) will also mark any
            quote they are in as Export Controlled (ITAR).
          </Text>
          <Checkbox isChecked={isChecked} onChange={handleChange}>
            Export Controlled (ITAR)
          </Checkbox>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup size="lg">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Tooltip
              label={
                isChecked === !!activeFile?.isExportControlled
                  ? 'You have not updated ITAR status.'
                  : ''
              }
            >
              <Button
                variant="primary"
                onClick={performBulkUpdate}
                isLoading={areFilesUpdating}
                isDisabled={isChecked === !!activeFile?.isExportControlled}
              >
                Save Changes
              </Button>
            </Tooltip>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
